import { Schema } from "effect"

// Market data shared by backend and frontend. All times are epoch seconds UTC.

// --- Markets and venues --------------------------------------------------------

export const Market = Schema.Literals(["IN", "US", "JP", "DE", "CFD"])
export type Market = typeof Market.Type

export const VenueId = Schema.Literals(["NSE", "BSE", "NYSE", "NASDAQ", "TSE", "XETR", "OANDA"])
export type VenueId = typeof VenueId.Type

export interface Session {
  /** Local wall-clock time "HH:MM" in the venue's timezone. */
  readonly open: string
  /** At or before `open` when the session runs past midnight: it closes on the next local day. */
  readonly close: string
  /** Local weekdays (0 = Sunday) the session opens on; default Monday to Friday. */
  readonly days?: ReadonlyArray<number>
}

export interface Venue {
  readonly id: VenueId
  readonly market: Market
  readonly name: string
  /** IANA timezone, used to align bars and to display times. */
  readonly timezone: string
  readonly currency: string
  /** Regular trading sessions; more than one when the venue has a lunch break. */
  readonly sessions: ReadonlyArray<Session>
}

// Venues are data: adding a market means adding an entry here plus a data provider.
export const VENUES: Readonly<Record<VenueId, Venue>> = {
  NSE: {
    id: "NSE",
    market: "IN",
    name: "National Stock Exchange of India",
    timezone: "Asia/Kolkata",
    currency: "INR",
    sessions: [{ open: "09:15", close: "15:30" }]
  },
  BSE: {
    id: "BSE",
    market: "IN",
    name: "BSE",
    timezone: "Asia/Kolkata",
    currency: "INR",
    sessions: [{ open: "09:15", close: "15:30" }]
  },
  NYSE: {
    id: "NYSE",
    market: "US",
    name: "New York Stock Exchange",
    timezone: "America/New_York",
    currency: "USD",
    sessions: [{ open: "09:30", close: "16:00" }]
  },
  NASDAQ: {
    id: "NASDAQ",
    market: "US",
    name: "Nasdaq",
    timezone: "America/New_York",
    currency: "USD",
    sessions: [{ open: "09:30", close: "16:00" }]
  },
  TSE: {
    id: "TSE",
    market: "JP",
    name: "Tokyo Stock Exchange",
    timezone: "Asia/Tokyo",
    currency: "JPY",
    sessions: [{ open: "09:00", close: "11:30" }, { open: "12:30", close: "15:30" }]
  },
  XETR: {
    id: "XETR",
    market: "DE",
    name: "Xetra (Deutsche Börse)",
    timezone: "Europe/Berlin",
    currency: "EUR",
    sessions: [{ open: "09:00", close: "17:30" }]
  },
  OANDA: {
    id: "OANDA",
    market: "CFD",
    name: "OANDA (CFDs)",
    timezone: "America/New_York",
    currency: "USD",
    // The FX trading day: 17:00 to 17:00 New York, opening Sunday to Thursday (docs/design/0010).
    sessions: [{ open: "17:00", close: "17:00", days: [0, 1, 2, 3, 4] }]
  }
}

/**
 * The quantity steps orders on a venue use (docs/design/0013): CFDs trade in fractions of a unit
 * (any multiple of 0.01; the steppers move by 0.1); everything else in whole lots. As text, exact.
 */
export const quantitySteps = (venue: VenueId, lotSize: number): { readonly step: string; readonly nudge: string; readonly fractional: boolean } => {
  if (VENUES[venue].market === "CFD") return { step: "0.01", nudge: "0.1", fractional: true }
  const lot = String(Math.max(1, Math.round(lotSize)))
  return { step: lot, nudge: lot, fractional: false }
}

// --- Instruments ----------------------------------------------------------------

/** "VENUE:SYMBOL", TradingView style, e.g. "NSE:RELIANCE". */
export const InstrumentId = Schema.String.pipe(Schema.brand("InstrumentId"))
export type InstrumentId = typeof InstrumentId.Type

export const InstrumentKind = Schema.Literals(["equity", "index", "etf", "other"])
export type InstrumentKind = typeof InstrumentKind.Type

export const Instrument = Schema.Struct({
  id: InstrumentId,
  venue: VenueId,
  symbol: Schema.String,
  name: Schema.String,
  kind: InstrumentKind,
  currency: Schema.String,
  tickSize: Schema.Number,
  lotSize: Schema.Number,
  /** Decimal places of a price; used when converting to BigDecimal for orders. */
  pricePrecision: Schema.Number
})
export type Instrument = typeof Instrument.Type

export const instrumentId = (venue: VenueId, symbol: string): InstrumentId => InstrumentId.make(`${venue}:${symbol}`)

// --- Timeframes and candles -------------------------------------------------------

export const TIMEFRAMES = ["1m", "3m", "5m", "15m", "1h", "4h", "1D"] as const
export const Timeframe = Schema.Literals(TIMEFRAMES)
export type Timeframe = typeof Timeframe.Type

export const TIMEFRAME_SECONDS: Readonly<Record<Timeframe, number>> = {
  "1m": 60,
  "3m": 180,
  "5m": 300,
  "15m": 900,
  "1h": 3_600,
  "4h": 14_400,
  "1D": 86_400
}

export const Candle = Schema.Struct({
  /** Bar open time. */
  time: Schema.Number,
  open: Schema.Number,
  high: Schema.Number,
  low: Schema.Number,
  close: Schema.Number,
  volume: Schema.Number
})
export type Candle = typeof Candle.Type

export const CandlesResponse = Schema.Struct({
  candles: Schema.Array(Candle),
  /** False when some closed bars in the range couldn't be loaded (e.g. broker disconnected). */
  complete: Schema.Boolean,
  /** Where the returned bars came from, e.g. ["fyers"] or ["yahoo"]. */
  sources: Schema.Array(Schema.String),
  /** With `asOf`: the last candle is still forming at that time (it grows as the replay moves). */
  forming: Schema.optionalKey(Schema.Boolean)
})
export type CandlesResponse = typeof CandlesResponse.Type
